import { useEffect, useState, useMemo, useCallback } from "react";
import { Page } from "@iq/models";
import { getProducts, createProductWithVariants, getProduct, uploadProductImage } from "../services/productService";
import { StoreCatalogCard } from "../modelsTesting";
import "../styles/catalog.css";
import { FiPlus } from "react-icons/fi";
import CreateProductModal from "../modals/CreateProduct/CreateProductModal";
import CreateVariantModal from "../modals/CreateProduct/CreateVariantModal";
import RenameProduct from "../modals/RenameProduct/RenameProduct";
import DeleteProduct from "../modals/DeleteProduct/DeleteProduct";
import useIsSmallScreen from "../hooks/useIsSmallScreen";
function Catalog({ onOpenProduct = () => {} }) {
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [products, setProducts] = useState([]);
    const [error, setError] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [isCreateProductModalOpen, setIsCreateProductModalOpen] = useState(false);
    const [isAddVariantModalOpen, setIsAddVariantModalOpen] = useState(false);
    const [createProductAttributes, setCreateProductAttributes] = useState([]);
    const [createProductVariants, setCreateProductVariants] = useState([]);
    const [newProductName, setNewProductName] = useState("");
    const [newProductDescription, setNewProductDescription] = useState("");
    const [goingBackFromVariantModal, setGoingBackFromVariantModal] = useState(false);
    const [createProductFormResetToken, setCreateProductFormResetToken] = useState(0);
    const [renameTarget, setRenameTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const fetchProducts = useCallback(async (isMountedRef) => {
        setLoadingProducts(true);
        setError("");

        try {
            const payload = await getProducts();

            if (isMountedRef && !isMountedRef.current) {
                return;
            }

            setProducts(payload?.products || []);
        } catch (err) {
            if (isMountedRef && !isMountedRef.current) {
                return;
            }

            setProducts([]);
            setError(err.message || "Failed to load products");
        } finally {
            if (!isMountedRef || isMountedRef.current) {
                setLoadingProducts(false);
            }
        }
    }, []);

    useEffect(() => {
        const isMountedRef = { current: true };

        fetchProducts(isMountedRef);

        return () => {
            isMountedRef.current = false;
        };
    }, [fetchProducts]);

    const handleCreated = useCallback(async () => {
        await fetchProducts();
        setIsCreateProductModalOpen(false);
    }, [fetchProducts]);

    const handleOpenCreateProductModal = useCallback(() => {
        setGoingBackFromVariantModal(false);
        setIsCreateProductModalOpen(true);
    }, []);

    const handleCreateProductNext = useCallback(() => {
        setIsCreateProductModalOpen(false);
        setGoingBackFromVariantModal(false);
        setIsAddVariantModalOpen(true);
    }, []);

    const handleCreateVariantModalBack = useCallback(() => {
        setIsAddVariantModalOpen(false);
        setGoingBackFromVariantModal(true);
        setIsCreateProductModalOpen(true);
    }, []);

    const resetCreateProductFlow = useCallback(() => {
        setCreateProductAttributes([]);
        setCreateProductVariants([]);
        setNewProductName("");
        setNewProductDescription("");
        setCreateProductFormResetToken((previousToken) => previousToken + 1);
    }, []);

    const handleSaveProductWithVariants = useCallback(async (variantsFromModal = []) => {
        const productName = String(newProductName || "").trim();
        const productDescription = String(newProductDescription || "").trim();
        const normalizedVariants = Array.isArray(variantsFromModal) ? variantsFromModal : [];

        const createdPayload = await createProductWithVariants({
            name: productName,
            description: productDescription,
            variants: normalizedVariants,
        });

        const createdProductId = Number(createdPayload?.product?.id || createdPayload?.product?.ID || 0);
        const variantsWithLocalImages = normalizedVariants.filter((variant) => {
            const uploadItems = Array.isArray(variant?.UploadImages)
                ? variant.UploadImages.filter((item) => item?.file instanceof File)
                : [];
            return uploadItems.length > 0;
        });

        if (createdProductId > 0 && variantsWithLocalImages.length > 0) {
            const failedUploads = [];
            try {
                const productDetailPayload = await getProduct(createdProductId);
                const savedVariants = Array.isArray(productDetailPayload?.product?.variants)
                    ? productDetailPayload.product.variants
                    : [];

                const variantIdBySku = new Map(
                    savedVariants.map((savedVariant) => [
                        String(savedVariant?.sku || savedVariant?.SKU || "").trim().toLowerCase(),
                        Number(savedVariant?.id || savedVariant?.ID || 0),
                    ])
                );

                for (const variant of variantsWithLocalImages) {
                    const sku = String(variant?.SKU || variant?.sku || "").trim().toLowerCase();
                    const variantId = Number(variantIdBySku.get(sku) || 0);
                    const uploadItems = Array.isArray(variant?.UploadImages)
                        ? variant.UploadImages
                            .filter((item) => item?.file instanceof File)
                            .map((item) => ({
                                file: item.file,
                                altText: String(item?.altText || "").trim(),
                            }))
                        : [];

                    if (!sku || variantId <= 0 || uploadItems.length === 0) {
                        continue;
                    }

                    try {
                        for (const imageItem of uploadItems) {
                            await uploadProductImage(createdProductId, imageItem.file, {
                                variantId,
                                altText: String(imageItem.altText || `Variant ${sku}`).trim(),
                            });
                        }
                    } catch (error) {
                        failedUploads.push(`${sku}: ${error?.message || "upload failed"}`);
                    }
                }
            } catch (error) {
                failedUploads.push(`Unable to resolve created variants: ${error?.message || "unknown error"}`);
            }

            if (failedUploads.length > 0) {
                throw new Error(`Product was created, but some images failed to upload. ${failedUploads.join(" | ")}`);
            }
        }

        await fetchProducts();
        setIsAddVariantModalOpen(false);
        resetCreateProductFlow();
    }, [fetchProducts, newProductDescription, newProductName, resetCreateProductFlow]);

    const filteredProducts = useMemo(() => {
            const normalizedQuery = searchQuery.trim().toLowerCase();
            if (!normalizedQuery) {
                    return products;
            }

            return products.filter((product) => {
                    const normalizedName = String(product?.name || "").toLowerCase();
                    return normalizedName.includes(normalizedQuery);
            });
    }, [products, searchQuery]);

    const isSmallScreen = useIsSmallScreen();

    return (
        <Page>
            <div className="catalogHeader">
                <input
                    className="catalogSearchInput"
                    type="text"
                    placeholder="Search catalog"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                {!isSmallScreen && (
                    <button
                        className="btn catalogCreateProductButton"
                        onClick={handleOpenCreateProductModal}
                    >
                        <span>Create Product</span>
                        <FiPlus size={18}/>
                    </button>
                )}
            </div>
            {loadingProducts ? <p>Loading products...</p> : null}
            {error ? <p>{error}</p> : null}
            {!loadingProducts && !error ? (
                <div className="catalogCardsContainer">
                    {filteredProducts.map((product) => (
                        <StoreCatalogCard
                            key={product.id}
                            name={product.name}
                            description={product.description}
                            image={product.image}
                            variantCount={product.variant_count}
                            activeVariantCount={product.active_variant_count}
                            pricing={product.pricing}
                            onClick={() => onOpenProduct(product.id || product.ID)}
                            onRename={() => setRenameTarget({ id: product.id || product.ID, name: product.name })}
                            onDelete={() => setDeleteTarget({ id: product.id || product.ID, name: product.name })}
                        />
                    ))}
                    {filteredProducts.length === 0 ? <p>No products match your search.</p> : null}
                </div>
            ) : null}

            <RenameProduct
                isOpen={!!renameTarget}
                onClose={() => setRenameTarget(null)}
                productId={renameTarget?.id}
                currentName={renameTarget?.name ?? ""}
                onRenameSuccess={() => {
                    setRenameTarget(null);
                    fetchProducts({ current: true });
                }}
            />
            <DeleteProduct
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                productId={deleteTarget?.id}
                productName={deleteTarget?.name ?? ""}
                onDeleteSuccess={() => {
                    setDeleteTarget(null);
                    fetchProducts({ current: true });
                }}
            />
            <CreateProductModal
                isOpen={isCreateProductModalOpen}
                onClose={() => setIsCreateProductModalOpen(false)}
                onCreated={handleCreated}
                onNext={handleCreateProductNext}
                attributes={createProductAttributes}
                onAttributesChange={setCreateProductAttributes}
                onProductNameChange={setNewProductName}
                onProductDescriptionChange={setNewProductDescription}
                fromBack={goingBackFromVariantModal}
                resetToken={createProductFormResetToken}
            />
            <CreateVariantModal
                isOpen={isAddVariantModalOpen}
                onClose={() => setIsAddVariantModalOpen(false)}
                attributes={createProductAttributes}
                onAttributesChange={setCreateProductAttributes}
                variants={createProductVariants}
                onVariantsChange={setCreateProductVariants}
                onBack={handleCreateVariantModalBack}
                onSave={handleSaveProductWithVariants}
                productName={newProductName}
                productDescription={newProductDescription}
                fromBack={goingBackFromVariantModal}
                animateIn={false}
                animateOut={!goingBackFromVariantModal}
            />
        </Page>
    )
}

export default Catalog
